import { useEffect } from 'react';
import { useShadowRoot } from '@backstage/plugin-techdocs-react';
import { readerStyles } from './readerStyles';

/*
 * Puts the Hone doc-body stylesheet inside the TechDocs shadow root.
 *
 * `@backstage/plugin-techdocs` writes its own <style> into the shadow
 * document's <head> while transforming the page, so appending there lands after
 * it and wins ties on source order without needing `!important` on every rule.
 * The shadow root is rebuilt on each page load, which re-runs this effect.
 */
export const ReaderStylesAddon = () => {
  const shadowRoot = useShadowRoot();

  useEffect(() => {
    if (!shadowRoot) return undefined;
    const style = document.createElement('style');
    style.dataset.honePortal = 'techdocs-reader';
    style.textContent = readerStyles;

    /*
     * These rules only win their ties while this is the last stylesheet, and
     * TechDocs swaps the whole cloned document — head included — on navigation
     * without the shadow root's identity changing. Re-resolving the head on
     * each change, rather than capturing it once, keeps the sheet attached and
     * last across those swaps.
     */
    const keepLast = () => {
      const head = shadowRoot.querySelector('head') ?? shadowRoot;
      if (head.lastElementChild !== style) head.appendChild(style);
    };
    keepLast();
    const observer = new MutationObserver(keepLast);
    observer.observe(shadowRoot, { childList: true });

    return () => {
      observer.disconnect();
      style.remove();
    };
  }, [shadowRoot]);

  useEffect(() => {
    if (!shadowRoot) return undefined;
    const host = shadowRoot.host;

    /*
     * The portal sets `data-theme-mode` on the outer document.body, which the
     * shadow root cannot see. Mirroring it onto the shadow host lets the dark
     * rules in readerStyles use `:host([data-theme-mode='dark'])`, which every
     * browser supports.
     */
    const syncThemeMode = () => {
      const mode = document.body.getAttribute('data-theme-mode');
      if (mode === null) host.removeAttribute('data-theme-mode');
      else host.setAttribute('data-theme-mode', mode);
    };
    syncThemeMode();
    const observer = new MutationObserver(syncThemeMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-theme-mode'],
    });

    return () => {
      observer.disconnect();
      host.removeAttribute('data-theme-mode');
    };
  }, [shadowRoot]);

  return null;
};
